import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import type { Job } from "@/lib/mockData";
import {
  fmtMinutes,
  fmtPct,
  fmtTokens,
  fmtUsd,
  rollupUsage,
} from "@/lib/usage";

interface Props {
  jobs: Job[];
}

/**
 * Compact usage + cost strip. Five headline metrics across the top of the
 * agent wall. Click to expand and see the breakdown (per-workflow cost,
 * runner-vs-token split, ROI math).
 */
export function UsageStrip({ jobs }: Props) {
  const [expanded, setExpanded] = useState(false);
  const rollup = useMemo(() => rollupUsage(jobs), [jobs]);

  return (
    <section className="mb-4 rounded-md border border-paper-line bg-paper-2">
      <div className="grid grid-cols-5 divide-x divide-paper-line">
        <Metric
          label="Jobs"
          value={String(rollup.jobsTotal)}
          hint={`${rollup.jobsSucceeded} done · ${rollup.jobsFailed} failed · ${rollup.jobsNotStarted} not started`}
        />
        <Metric
          label="Success rate"
          value={fmtPct(rollup.successRate)}
          hint={`${rollup.jobsSucceeded} of ${rollup.jobsSucceeded + rollup.jobsFailed} resolved`}
          tone={rollup.successRate >= 0.9 ? "good" : rollup.successRate >= 0.7 ? "warn" : "bad"}
        />
        <Metric
          label="Runner time"
          value={fmtMinutes(rollup.runnerMinutes)}
          hint={`${fmtTokens(rollup.tokens)} tokens`}
        />
        <Metric
          label="Flow cost"
          value={fmtUsd(rollup.flowUsd)}
          hint="tokens + VM"
        />
        <Metric
          label="Value delivered"
          value={fmtUsd(rollup.savingsUsd)}
          hint={`${rollup.hoursSaved.toFixed(1)}h of supervisor time`}
          tone="good"
        />
      </div>

      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-center gap-1 border-t border-paper-line py-1.5 text-[11px] uppercase tracking-wider text-ink-3 hover:bg-paper-tint"
      >
        {expanded ? (
          <>
            <ChevronUp size={12} /> Hide breakdown
          </>
        ) : (
          <>
            <ChevronDown size={12} /> Show breakdown
          </>
        )}
      </button>

      {expanded && <Breakdown jobs={jobs} rollup={rollup} />}
    </section>
  );
}

function Metric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "good" | "warn" | "bad";
}) {
  const toneCls =
    tone === "good"
      ? "text-status-success"
      : tone === "warn"
        ? "text-status-approval"
        : tone === "bad"
          ? "text-status-failed"
          : "text-ink-1";
  return (
    <div className="flex min-w-0 flex-col gap-0.5 px-4 py-3">
      <span className="text-[11px] uppercase tracking-wider text-ink-4">{label}</span>
      <span className={`font-mono text-[22px] leading-tight ${toneCls}`}>{value}</span>
      {hint && <span className="truncate text-[11px] text-ink-3">{hint}</span>}
    </div>
  );
}

function Breakdown({ jobs, rollup }: { jobs: Job[]; rollup: ReturnType<typeof rollupUsage> }) {
  // Per-workflow cost rollup.
  const byWorkflow = useMemo(() => {
    const acc = new Map<string, { name: string; count: number; flow: number; savings: number }>();
    for (const j of jobs) {
      const c = rollupUsage([j]);
      const prev = acc.get(j.workflow.id) ?? { name: j.workflow.name, count: 0, flow: 0, savings: 0 };
      acc.set(j.workflow.id, {
        name: j.workflow.name,
        count: prev.count + 1,
        flow: prev.flow + c.flowUsd,
        savings: prev.savings + c.savingsUsd,
      });
    }
    return Array.from(acc.values()).sort((a, b) => b.flow - a.flow);
  }, [jobs]);

  const runnerUsd = (rollup.runnerMinutes / 60) * 0.11;
  const tokenUsd = Math.max(0, rollup.flowUsd - runnerUsd);
  const roi = rollup.flowUsd > 0 ? rollup.savingsUsd / rollup.flowUsd : 0;

  return (
    <div className="grid grid-cols-2 gap-6 border-t border-paper-line p-4 text-sm">
      <div>
        <div className="mb-2 flex items-center gap-1 text-[11px] uppercase tracking-wider text-ink-3">
          Cost split
          <Info size={10} className="text-ink-4" aria-hidden />
        </div>
        <Row
          label="Azure OpenAI tokens"
          right={fmtUsd(tokenUsd)}
          sub={`${fmtTokens(rollup.tokens)} total`}
        />
        <Row
          label="Runner VM time"
          right={fmtUsd(runnerUsd)}
          sub={fmtMinutes(rollup.runnerMinutes)}
        />
        <div className="mt-2 flex items-center justify-between border-t border-paper-line pt-2 font-medium">
          <span className="text-ink-1">Total Flow cost</span>
          <span className="font-mono text-ink-1">{fmtUsd(rollup.flowUsd)}</span>
        </div>

        <div className="mt-4 rounded-sm border border-teal-100 bg-status-success-bg p-3 text-xs text-ink-2">
          <div className="font-medium text-ink-1">
            {roi > 0 ? `${roi.toFixed(0)}× return` : "No value captured yet"}
          </div>
          <div className="mt-0.5">
            {rollup.hoursSaved.toFixed(1)}h of manual work replaced for{" "}
            {fmtUsd(rollup.flowUsd)}. That saved{" "}
            {fmtUsd(rollup.savingsUsd)} vs running the same volume by hand.
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 text-[11px] uppercase tracking-wider text-ink-3">
          Cost by workflow
        </div>
        {byWorkflow.length === 0 ? (
          <div className="text-xs text-ink-3">No jobs yet.</div>
        ) : (
          byWorkflow.map((w) => (
            <Row
              key={w.name}
              label={w.name}
              sub={`${w.count} run${w.count === 1 ? "" : "s"}`}
              right={fmtUsd(w.flow)}
              rightSub={`+${fmtUsd(w.savings)} saved`}
            />
          ))
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  sub,
  right,
  rightSub,
}: {
  label: string;
  sub?: string;
  right: string;
  rightSub?: string;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="min-w-0">
        <div className="truncate text-ink-1">{label}</div>
        {sub && <div className="text-[11px] text-ink-3">{sub}</div>}
      </div>
      <div className="shrink-0 text-right">
        <div className="font-mono text-ink-1">{right}</div>
        {rightSub && <div className="text-[11px] text-status-success">{rightSub}</div>}
      </div>
    </div>
  );
}
